// Investigate modifying PWS to import breakpoint values to maintain a source of truth.

const ORDERED_BREAKPOINT_NAMES = ['base', 'extraSmall', 'small', 'medium', 'large', 'extraLarge'] as const;
export type BreakpointName = (typeof ORDERED_BREAKPOINT_NAMES)[number];

type WidthRange = {
  minWidth: number;
  contentWidth: number;
  maxWidth: number;
};

type LayoutHelper = {
  BREAKPOINT_WIDTHS: Record<BreakpointName, WidthRange>;
  BREAKPOINT_ORDER: readonly BreakpointName[];
  breakpointNames: () => BreakpointName[];
  breakpointWidths: () => LayoutBreakpointWidth[];
  breakpoints: () => LayoutBreakpoint[];
  findClosestBreakpoint: (
    breakpointName: BreakpointName,
    amongBreakpoints: BreakpointName[]
  ) => BreakpointName | undefined;
  getCurrentBreakpoint: (windowWidth: number) => BreakpointName;
};

const layoutHelpers: LayoutHelper = {
  BREAKPOINT_WIDTHS: {
    base: {
      minWidth: 1,
      contentWidth: 300,
      maxWidth: 373,
    },
    extraSmall: {
      minWidth: 374,
      contentWidth: 354,
      maxWidth: 739,
    },
    small: {
      minWidth: 740,
      contentWidth: 680,
      maxWidth: 1023,
    },
    medium: {
      minWidth: 1024,
      contentWidth: 960,
      maxWidth: 1279,
    },
    large: {
      minWidth: 1280,
      contentWidth: 1200,
      maxWidth: 1679,
    },
    extraLarge: {
      minWidth: 1680,
      contentWidth: 1400,
      maxWidth: 1920,
    },
  },

  BREAKPOINT_ORDER: ORDERED_BREAKPOINT_NAMES,

  breakpointNames: (): BreakpointName[] => [...ORDERED_BREAKPOINT_NAMES],
  breakpointWidths: (): LayoutBreakpointWidth[] =>
    ORDERED_BREAKPOINT_NAMES.map((breakpointName) => layoutHelpers.BREAKPOINT_WIDTHS[breakpointName]),

  breakpoints: (): LayoutBreakpoint[] =>
    ORDERED_BREAKPOINT_NAMES.map((breakpointName) => ({
      breakpointName,
      breakpointWidth: layoutHelpers.BREAKPOINT_WIDTHS[breakpointName],
    })),

  /**
   * Given a breakpoint name and an arrow of breakpoints under consideration, this helper finds the nearest fallback
   * breakpoint among the available breakpoints under consideration.
   *
   * For example: if availableBreakpoints=['base', 'medium', 'extra-large'] and breakpointName='small', then this helper
   * should return 'base' as the closest available breakpoint.
   *
   * @param breakpointName Desired breakpoint
   * @param availableBreakpoints An array of breakpoints to be considered
   * @returns Name of the closest breakpoint
   */
  findClosestBreakpoint: (
    breakpointName: BreakpointName,
    availableBreakpoints: BreakpointName[] = [...layoutHelpers.BREAKPOINT_ORDER]
  ): BreakpointName | undefined => {
    if (availableBreakpoints.includes(breakpointName)) {
      return breakpointName;
    }
    const breakpoints = [...layoutHelpers.BREAKPOINT_ORDER].reverse();
    const subset = breakpoints.slice(breakpoints.indexOf(breakpointName));
    const closestBreakpoint = subset.find((breakpointName) => availableBreakpoints.includes(breakpointName));
    return closestBreakpoint;
  },

  getCurrentBreakpoint: (windowWidth: number) => {
    const currentBreakpoint = layoutHelpers.BREAKPOINT_ORDER.find((breakpointName) => {
      const refBreakpointWidths = layoutHelpers.BREAKPOINT_WIDTHS[breakpointName];
      return refBreakpointWidths.minWidth <= windowWidth && windowWidth <= refBreakpointWidths.maxWidth;
    });

    return currentBreakpoint || 'extraLarge';
  },
} as const;

export default layoutHelpers;
